import React, {
  ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Drawer, Modal } from 'antd';
import Form, { FormSchema } from '@sezenta/antd-schema-form';
import { useConnection } from '../connection/connection';
import { setFormValidationErrors } from '../common/Utils';

export type ChangePasswordModalInstance = {
  open: () => void;
};

type Props = {};

const ChangePasswordModal: ForwardRefRenderFunction<
  ChangePasswordModalInstance,
  Props
> = (props, ref) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [form] = Form.useForm<any>();
  const connection = useConnection();

  useImperativeHandle(
    ref,
    () => ({
      open: () => {
        setVisible(true);
        setSaving(false);
        form.resetFields();
        form.setFieldsValue({});
      },
    }),
    [form],
  );

  const schema = useMemo(() => {
    const formSchema: FormSchema = [
      {
        id: 'currentPassword',
        type: 'password',
        name: 'Current Password',
        props: { placeholder: 'Current Password' },
        options: {
          rules: [{ required: true, message: 'Current Password is required' }],
        },
      },
      {
        id: 'password',
        type: 'password',
        name: 'New Password',
        props: { placeholder: 'New Password' },
        options: {
          rules: [{ required: true, message: 'New Password is required' }],
        },
      },
      {
        id: 'confirmPassword',
        type: 'password',
        name: 'Confirm New Password',
        props: { placeholder: 'Confirm New Password' },
        options: {
          dependencies: ['password'],
          rules: [
            { required: true, message: 'Please confirm your password.' },
            {
              validator: 'equals',
              to: 'password',
              message: 'Your password and confirm password does not match',
            },
          ],
        },
      },
    ];
    return formSchema;
  }, []);

  const submit = useCallback(
    (values: any) => {
      setSaving(true);
      connection
        .post('auth/change-password', values)
        .then(() => setVisible(false))
        .catch((err) => {
          if (!setFormValidationErrors(form, err)) {
            Modal.error({
              title: 'Error',
              content: 'Something went wrong. Please try again later.',
            });
          }
        })
        .finally(() => setSaving(false));
    },
    [connection, form],
  );
  const close = useCallback(() => {
    setVisible(false);
  }, []);
  return (
    <Drawer
      title={'Change Password'}
      open={visible}
      onClose={close}
      destroyOnClose={true}
      footer={
        <div className="flex justify-between gap-1">
          <div className="w-full flex justify-end gap-1">
            <Button onClick={close}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={form.submit}>
              Change
            </Button>
          </div>
        </div>
      }
      width={600}
    >
      <Form onFinish={submit} form={form} layout="vertical">
        <Form.Items schema={schema} />
      </Form>
    </Drawer>
  );
};

export const useChangePasswordModal = () =>
  useRef<ChangePasswordModalInstance>(null);

export default React.forwardRef<ChangePasswordModalInstance, Props>(
  ChangePasswordModal,
);
